import React, { useState } from 'react';
import {
  CheckCircle2,
  Plus,
  X,
  Clock,
} from 'lucide-react';
import { NiyamSubmission, NiyamRule, AdminRole } from '../types';
import { Badge, Button, Input } from '../../components/core';

interface NiyamTrackingProps {
  submissions: NiyamSubmission[];
  rules: NiyamRule[];
  currentRole: AdminRole;
  onApproveNiyam: (sub: NiyamSubmission) => void;
  onRejectNiyam: (sub: NiyamSubmission) => void;
  onAddRule: (rule: NiyamRule) => void;
  addToast: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

export const NiyamTracking: React.FC<NiyamTrackingProps> = ({
  submissions,
  rules,
  currentRole,
  onApproveNiyam,
  onRejectNiyam,
  onAddRule,
  addToast,
}) => {
  const [subTab, setSubTab] = useState<'submissions' | 'rules'>('submissions');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isAuditor = currentRole === 'Auditor';

  const [ruleForm, setRuleForm] = useState({
    title: '',
    level: 'All Levels',
    defaultPoints: 10,
    autoApprove: true,
    description: '',
  });

  const handleApprove = (sub: NiyamSubmission) => {
    if (isAuditor) return;
    onApproveNiyam(sub);
    addToast('success', 'Niyam Verified', `Approved ${sub.studentName}'s Niyam submission (+${sub.pointsEarned} pts)`);
  };

  const handleReject = (sub: NiyamSubmission) => {
    if (isAuditor) return;
    onRejectNiyam(sub);
    addToast('info', 'Niyam Rejected', `Rejected submission for ${sub.studentName}`);
  };

  const handleRuleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isAuditor) return;

    const newRule: NiyamRule = {
      id: `RUL-${rules.length + 1}`,
      ...ruleForm,
    };
    onAddRule(newRule);
    addToast('success', 'Niyam Rule Created', `Created ${newRule.title} rule.`);
    setIsModalOpen(false);
  };

  const statusVariant = (status: string, isPending: boolean): 'success' | 'warning' | 'danger' => {
    if (status === 'Auto-Approved' || status === 'Approved') return 'success';
    if (isPending) return 'warning';
    return 'danger';
  };

  return (
    <div className="space-y-6">
      <div className="admin-card flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-[var(--gv-ink)]">Daily Niyam Vows & Discipline Tracking</h1>
          <p className="mt-0.5 text-xs text-[var(--gv-ink-muted)]">Audit daily student spiritual vows and configure auto-approval cutoffs.</p>
        </div>

        <div className="flex items-center rounded-xl border border-[var(--gv-border)] bg-stone-100 p-1">
          <button
            type="button"
            onClick={() => setSubTab('submissions')}
            className={`flex cursor-pointer items-center gap-2 rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-all ${
              subTab === 'submissions' ? 'admin-tab-active' : 'admin-tab-idle'
            }`}
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            <span>Check-in Queue ({submissions.filter((s) => s.status.includes('Pending')).length})</span>
          </button>
          <button
            type="button"
            onClick={() => setSubTab('rules')}
            className={`flex cursor-pointer items-center gap-2 rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-all ${
              subTab === 'rules' ? 'admin-tab-active' : 'admin-tab-idle'
            }`}
          >
            <Clock className="h-3.5 w-3.5" />
            <span>Niyam Rules Config ({rules.length})</span>
          </button>
        </div>
      </div>

      {subTab === 'submissions' && (
        <div className="admin-card overflow-hidden">
          <div className="flex items-center justify-between border-b border-[var(--gv-border)] bg-stone-50/50 p-4">
            <h3 className="text-sm font-bold text-[var(--gv-ink)]">Daily Niyam Check-ins Log</h3>
            <Badge variant="warning">⏰ Daily Cutoff: 07:30 PM (Sunset)</Badge>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-[var(--gv-ink-muted)]">
              <thead className="border-b border-[var(--gv-border)] bg-stone-50 font-mono text-[10px] uppercase tracking-wider text-[var(--gv-ink-muted)]">
                <tr>
                  <th className="px-4 py-2.5 font-bold">Log ID</th>
                  <th className="px-4 py-2.5 font-bold">Student Name & Level</th>
                  <th className="px-4 py-2.5 font-bold">Date</th>
                  <th className="px-4 py-2.5 font-bold">Completed Niyams / Vows</th>
                  <th className="px-4 py-2.5 text-center font-bold">Points Earned</th>
                  <th className="px-4 py-2.5 font-bold">Status</th>
                  <th className="px-4 py-2.5 text-right font-bold">Verification Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 bg-white font-medium">
                {submissions.map((sub) => {
                  const isPending = sub.status.includes('Pending');

                  return (
                    <tr key={sub.id} className="transition-colors hover:bg-stone-50/80">
                      <td className="px-4 py-3 font-mono font-bold text-[var(--gv-brand)]">{sub.id}</td>
                      <td className="px-4 py-3">
                        <div className="font-bold text-[var(--gv-ink)]">{sub.studentName}</div>
                        <div className="text-[10px] text-[var(--gv-ink-muted)]">{sub.level}</div>
                      </td>
                      <td className="px-4 py-3 font-mono text-[var(--gv-ink-muted)]">{sub.submissionDate}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {sub.completedNiyams.map((n, i) => (
                            <Badge key={i} variant="outline" className="normal-case">{n}</Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center font-mono font-bold text-amber-700">
                        +{sub.pointsEarned} pts
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={statusVariant(sub.status, isPending)}>{sub.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isPending && !isAuditor ? (
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" onClick={() => handleApprove(sub)}>
                              Approve
                            </Button>
                            <Button size="sm" variant="outline" className="text-rose-700" onClick={() => handleReject(sub)}>
                              Reject
                            </Button>
                          </div>
                        ) : (
                          <span className="font-mono text-[11px] text-[var(--gv-ink-muted)]">Verified</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {subTab === 'rules' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-[var(--gv-ink)]">Daily Niyam Rules & Points Mapping</h2>
            {!isAuditor && (
              <Button size="sm" leftIcon={Plus} onClick={() => setIsModalOpen(true)}>
                Create Niyam Rule
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {rules.map((rule) => (
              <div key={rule.id} className="admin-card space-y-3 p-5">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] font-bold text-[var(--gv-brand)]">{rule.id} • {rule.level}</span>
                  <Badge variant="warning">+{rule.defaultPoints} Points</Badge>
                </div>

                <h3 className="text-base font-bold text-[var(--gv-ink)]">{rule.title}</h3>
                <p className="text-xs font-normal text-[var(--gv-ink-muted)]">{rule.description}</p>

                <div className="flex items-center justify-between border-t border-stone-100 pt-2 font-mono text-xs">
                  <span className="text-[var(--gv-ink-muted)]">Verification:</span>
                  <span className={`font-bold ${rule.autoApprove ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {rule.autoApprove ? 'Auto-Approve ✓' : 'Manual Review 🔍'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-[9990] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-xs">
          <div className="admin-card w-full max-w-md space-y-4 p-6 text-[var(--gv-ink)] shadow-xl">
            <div className="flex items-center justify-between border-b border-[var(--gv-border)] pb-3">
              <h3 className="text-base font-bold text-[var(--gv-ink)]">Create Daily Niyam Rule</h3>
              <Button variant="ghost" size="icon" leftIcon={X} aria-label="Close" onClick={() => setIsModalOpen(false)} />
            </div>

            <form onSubmit={handleRuleSubmit} className="space-y-3 text-xs">
              <Input
                label="Rule Title / Vow"
                type="text"
                required
                value={ruleForm.title}
                onChange={(e) => setRuleForm({ ...ruleForm, title: e.target.value })}
                placeholder="e.g., Temple Darshan"
              />

              <div className="grid grid-cols-2 gap-3">
                <div className="w-full space-y-1.5">
                  <label className="block text-xs font-bold text-[var(--gv-ink)]">Target Level</label>
                  <select
                    value={ruleForm.level}
                    onChange={(e) => setRuleForm({ ...ruleForm, level: e.target.value })}
                    className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                  >
                    <option value="All Levels">All Levels</option>
                    <option value="Level 1 & Level 2">Level 1 & 2</option>
                    <option value="Level 3 Only">Level 3 Only</option>
                  </select>
                </div>
                <Input
                  label="Points Value"
                  type="number"
                  required
                  className="font-mono"
                  value={ruleForm.defaultPoints}
                  onChange={(e) => setRuleForm({ ...ruleForm, defaultPoints: Number(e.target.value) })}
                />
              </div>

              <div className="w-full space-y-1.5">
                <label className="block text-xs font-bold text-[var(--gv-ink)]">Description</label>
                <textarea
                  rows={2}
                  value={ruleForm.description}
                  onChange={(e) => setRuleForm({ ...ruleForm, description: e.target.value })}
                  className="admin-input w-full rounded-xl border border-[var(--gv-border)] bg-white p-3 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                />
              </div>

              <label className="flex cursor-pointer items-center gap-2 font-semibold text-[var(--gv-ink)]">
                <input
                  type="checkbox"
                  checked={ruleForm.autoApprove}
                  onChange={(e) => setRuleForm({ ...ruleForm, autoApprove: e.target.checked })}
                  className="rounded border-stone-300 text-[var(--gv-brand)] focus:ring-[var(--gv-brand)]"
                />
                <span>Auto-Approve check-ins for this rule</span>
              </label>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Save Niyam Rule</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
